import { API_BASE_URL } from './constants';
import { getActiveCompanyId, setActiveCompanyId } from './active-company';

type ApiResponse<T> = {
  success: true;
  data: T;
  meta?: {
    pagination: {
      page: number;
      per_page: number;
      total: number;
      total_pages: number;
    };
  };
};

type ApiError = {
  success: false;
  error: string;
  code: string;
};

type ApiResult<T> = ApiResponse<T> | ApiError;

let getAccessTokenFn: (() => Promise<string>) | null = null;

export function setTokenGetter(fn: () => Promise<string>) {
  getAccessTokenFn = fn;
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  if (!getAccessTokenFn) return {};
  try {
    const token = await getAccessTokenFn();
    return { Authorization: `Bearer ${token}` };
  } catch {
    return {};
  }
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    method: string,
    path: string,
    options?: { body?: unknown; params?: Record<string, string | number | undefined> },
    isRetry = false,
  ): Promise<ApiResponse<T>> {
    const url = new URL(`${this.baseUrl}${path}`);

    if (options?.params) {
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.set(key, String(value));
        }
      });
    }

    const authHeaders = await getAuthHeaders();

    const headers: Record<string, string> = { ...authHeaders };

    // Multiempresa: scope the request to the active company when one is chosen.
    // When null, the header is omitted and the backend falls back to primary.
    const activeCompanyId = getActiveCompanyId();
    if (activeCompanyId !== null) {
      headers['X-Active-Company'] = String(activeCompanyId);
    }

    const body = options?.body ? JSON.stringify(options.body) : undefined;
    if (body) {
      headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(url.toString(), {
      method,
      headers,
      body,
    });

    if (response.status === 401) {
      // Distinguish a deactivated/expired account from a plain missing session.
      // ACCOUNT_INACTIVE means the IdP session is still valid, so sending the
      // user back to /login would re-authenticate and loop forever. Route to a
      // terminal screen instead.
      let code: string | undefined;
      try {
        code = ((await response.json()) as { code?: string })?.code;
      } catch {
        // no/!json body — treat as a plain unauthorized
      }
      window.location.href = code === 'ACCOUNT_INACTIVE' ? '/login?reason=inactive' : '/login';
      throw new Error('Unauthorized');
    }

    // Multiempresa lockout recovery: a 403 while an active company is stored
    // usually means that company is no longer a valid membership (removed, or
    // stale localStorage from another session). Clear the stored selection and
    // retry once without the header so the backend falls back to the primary
    // company — instead of hard-locking the user out of every page until they
    // manually clear cookies/localStorage.
    if (response.status === 403 && !isRetry && getActiveCompanyId() !== null) {
      setActiveCompanyId(null);
      return this.request<T>(method, path, options, true);
    }

    // 204 No Content — no body to parse
    if (response.status === 204) {
      return { success: true, data: undefined as T };
    }

    const result: ApiResult<T> = await response.json();

    if (!result.success) {
      throw new ApiRequestError(result.error, result.code, response.status);
    }

    return result;
  }

  async get<T>(path: string, params?: Record<string, string | number | undefined>) {
    return this.request<T>('GET', path, { params });
  }

  async post<T>(path: string, body?: unknown) {
    return this.request<T>('POST', path, { body });
  }

  async put<T>(path: string, body?: unknown) {
    return this.request<T>('PUT', path, { body });
  }

  async patch<T>(path: string, body?: unknown) {
    return this.request<T>('PATCH', path, { body });
  }

  async delete<T>(path: string) {
    return this.request<T>('DELETE', path);
  }
}

export class ApiRequestError extends Error {
  code: string;
  status: number;

  constructor(message: string, code: string, status: number) {
    super(message);
    this.name = 'ApiRequestError';
    this.code = code;
    this.status = status;
  }
}

export const api = new ApiClient(API_BASE_URL);
